import cn from "classnames"
import React, { useState } from "react"
import {
  type ShoppingItemCreateMutationVariables,
  type ShoppingItemUpdateMutationVariables,
  type ShoppingItemDeleteMutationVariables,
} from "@/api/graphql"
import validations from "@/common/util/validations"

const imageAccept = ".jpeg,.jpg,.png,image/jpeg,image/jpeg,image/png"

interface FormProps {
  exampleId: string
  id: string
  brandId: number
  tagTitle: string
  title: string
  description: string
  price: string
  priceVat: string
  image: boolean
  imageUrl?: string
  productType: string
  onDeleteNew: () => void
  onCreate: (variables: ShoppingItemCreateMutationVariables) => void
  onUpdate: (variables: ShoppingItemUpdateMutationVariables) => void
  onDelete: (variables: ShoppingItemDeleteMutationVariables) => void
}

const validateProductType = (productType: string) => {
  if (validations.shoppingItemType.test(productType) && productType.length <= 750 && productType.length > 0) {
    return false
  }
  return true
}

const Form: React.FC<FormProps> = ({
  exampleId,
  id,
  brandId,
  tagTitle,
  title,
  description,
  price,
  priceVat,
  image,
  imageUrl,
  productType,
  onDeleteNew,
  onCreate,
  onUpdate,
  onDelete,
}) => {
  const [formTitle, setFormTitle] = useState(title)
  const [formDescription, setFormDescription] = useState(description)
  const [formProductType, setFormProductType] = useState(productType)
  const [productTypeError, setProductTypeError] = useState<boolean>(validateProductType(productType))
  const [formImage, setFormImage] = useState<File | undefined>()

  const newId = id.startsWith("new")

  const deleteImage = (e: React.MouseEvent) => {
    e.preventDefault()

    onUpdate({
      id,
      title: formTitle,
      description: formDescription,
      productType: formProductType,
      deleteImage: true,
    })
  }

  const updateImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files || []
    setFormImage(files[0])
  }

  const deleteShoppingItem = () => {
    newId ? onDeleteNew() : onDelete({ id })
  }

  const saveShoppingItem = () => {
    if (productTypeError) {
      alert("Product type must match pattern: Group1 > Group2 > Group3 and be less then 750 characters")
      return
    }

    if (newId) {
      if (formTitle.length === 0 || formDescription.length === 0) {
        alert("Du har inte angett titel eller beskrivning.")
        return
      }

      const vars: ShoppingItemCreateMutationVariables = {
        exampleId,
        title: formTitle,
        description: formDescription,
        productType: formProductType,
      }

      if (formImage) {
        vars.image = formImage
      }

      onCreate(vars)
    } else {
      const vars: ShoppingItemUpdateMutationVariables = {
        id,
        title: formTitle,
        description: formDescription,
        productType: formProductType,
      }

      if (formImage) {
        vars.image = formImage
      }

      onUpdate(vars)
    }
  }

  return (
    <>
      <div className="field tagtitle">
        <strong>Tag title:</strong> <span>{tagTitle}</span>
      </div>
      <div className="field brandid">
        <strong>ID:</strong> <span>{newId ? "-" : brandId}</span>
      </div>
      <div className="field">
        <label htmlFor="title">
          <strong>Title:</strong>
        </label>
        <input
          type="text"
          name="title"
          id="shopping-title"
          value={formTitle}
          onChange={e => setFormTitle(e.target.value)}
        />
      </div>
      <div className="field product_type">
        <label htmlFor="product_type">
          <strong>Product type:</strong>
        </label>
        <input
          type="text"
          name="product_type"
          id="shopping-product_type"
          className={cn({ field_error: productTypeError })}
          value={formProductType}
          onChange={e => {
            setFormProductType(e.target.value)
            setProductTypeError(validateProductType(e.target.value))
          }}
        />
      </div>
      <div className="field description">
        <label htmlFor="description">Description:</label>
        <textarea
          name="description"
          id="shopping-description"
          value={formDescription}
          onChange={e => setFormDescription(e.target.value)}
        />
      </div>
      <div className="field price">
        <label>
          <strong>Price:</strong>
        </label>
        <p>
          <strong>{price}</strong> {priceVat}
        </p>
      </div>
      <div className="field image">
        <label htmlFor="image">
          <strong>Image (Format: JPEG or PNG. Image must be square):</strong>
        </label>
        {image ? (
          <div className="links">
            <a href={imageUrl}>{imageUrl}</a>
            <a href="" onClick={e => deleteImage(e)}>
              Delete
            </a>
          </div>
        ) : (
          <input type="file" name="image" id="shopping-image" accept={imageAccept} onChange={e => updateImage(e)} />
        )}
      </div>
      <div className="buttons">
        <a className="remove" onClick={() => deleteShoppingItem()}>
          Delete
        </a>
        <a className="save" onClick={() => saveShoppingItem()}>
          Save
        </a>
      </div>
    </>
  )
}

export default Form
